import React from 'react';
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import { ChangeYearValue } from '../models/OverallPerformanceModel';
import { roundToOneDecimal, changeColumnColorStatus, renderEmptyValue } from './appUtils';
import pagesStandardStyles from '../pages/Pages.module.css';

// Row interface for DataGrid - generic type parameter
interface GridRow {
  id: number;
  metric: string;
  metricKey: string;
}

/**
 * Renders a formatted numeric value that ellipsis-truncates to its column width.
 * When (and only when) the text is actually cut off — common on the Goal, Prior,
 * and Current columns on narrow/mobile layouts — hovering reveals the full value
 * in a tooltip. Truncation is detected by comparing the span's scrollWidth to its
 * clientWidth, re-checked on resize.
 *
 * Shared across DataGrids: import and pass the formatted display string as `value`.
 */
export const OverflowTooltipValue: React.FC<{ value: string }> = ({ value }) => {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkOverflow = (): void => {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    };

    checkOverflow();

    // ResizeObserver isn't available in every environment (e.g. Jest/JSDOM,
    // some embedded/older browsers). Guard against it being undefined so the
    // cell still renders (with the one-time overflow check above) instead of
    // throwing when the grid mounts.
    if (typeof ResizeObserver === 'undefined') return undefined;

    // A ResizeObserver re-checks after layout settles whenever the cell/column
    // changes size (window resize, responsive column widths, etc.). A plain
    // window 'resize' listener measures too early — before MUI X widens the
    // columns — so a no-longer-truncated value would wrongly keep its tooltip.
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [value]);

  // The Tooltip is always mounted (keeping the observed span stable); a null
  // title disables it, so the tooltip only appears while the value is truncated.
  return (
    <Tooltip
      title={isOverflowing ? value : null}
      arrow
      placement="top"
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      // Match the dark tooltip ace-ui-web's MuiButton shows for truncated button
      // text (e.g. the "Evaluate Customers" Take Action button): black background
      // and arrow, via the theme's common.black palette token.
      slotProps={{
        tooltip: { sx: { backgroundColor: 'common.black' } },
        arrow: { sx: { color: 'common.black' } },
      }}>
      <span
        ref={textRef}
        className={pagesStandardStyles.truncatedCellValue}>
        {value}
      </span>
    </Tooltip>
  );
};

/**
 * Renders benchmark percentage cells with % symbol and one decimal place
 * Uses the same logic as the Change (Over Last Year) % column
 * @param params - MUI DataGrid cell render parameters
 * @returns Formatted percentage string with one decimal place
 */
export const renderPercentCell = (params: GridRenderCellParams<GridValidRowModel, number>) => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  return `${roundToOneDecimal(params.value)}%`;
};

/**
 * Renders change percentage cells with color based on status
 * Generic renderer for any DataGrid column showing year-over-year change percentages
 * @param params - MUI DataGrid cell render parameters with ChangeYearValue
 * @returns JSX span element with colored percentage text
 */
export const renderChangePercentCell = (params: GridRenderCellParams<GridRow, ChangeYearValue>) => {
  if (!params.value) {
    return renderEmptyValue(null);
  }

  const emptyCheck = renderEmptyValue(params.value.value);
  if (emptyCheck) return emptyCheck;

  const { value, status } = params.value;

  // Round to one decimal place and add % symbol
  const roundedValue = roundToOneDecimal(value);
  const displayValue = `${roundedValue}%`;

  return <span style={{ color: changeColumnColorStatus(status) }}>{displayValue}</span>;
};
